// DDoS Protection Utility
class DDosProtection {
  constructor() {
    this.requestCounts = new Map();
    this.blockedIPs = new Set();
    this.cache = new Map();
    this.requestQueue = new Map();
    
    // Configuration
    this.config = {
      maxRequestsPerMinute: 60,
      maxRequestsPerSecond: 10,
      blockDuration: 5 * 60 * 1000, // 5 minutes
      requestTimeout: 10000, // 10 seconds
      cacheTimeout: 5 * 60 * 1000, // 5 minutes
      maxConcurrentRequests: 5,
      retryAttempts: 3,
      retryDelay: 1000
    };
    
    // Clean up old entries every minute
    setInterval(() => this.cleanup(), 60000);
  }

  // Get client identifier (in browser, use session/localStorage)
  getClientId() {
    let clientId = localStorage.getItem('clientId');
    if (!clientId) {
      clientId = 'client_' + Math.random().toString(36).substr(2, 9) + '_' + Date.now();
      localStorage.setItem('clientId', clientId);
    }
    return clientId;
  }

  // Check if request is allowed
  isRequestAllowed(endpoint = 'default') {
    const clientId = this.getClientId();
    const now = Date.now();
    const key = `${clientId}_${endpoint}`;
    
    // Check if client is blocked
    if (this.blockedIPs.has(clientId)) {
      const blockInfo = this.requestCounts.get(`${clientId}_blocked`);
      if (blockInfo && now - blockInfo.timestamp < this.config.blockDuration) {
        return { allowed: false, reason: 'Client temporarily blocked', retryAfter: this.config.blockDuration };
      } else {
        // Unblock client
        this.blockedIPs.delete(clientId);
        this.requestCounts.delete(`${clientId}_blocked`);
      }
    }

    // Initialize or get request count
    if (!this.requestCounts.has(key)) {
      this.requestCounts.set(key, { count: 0, timestamp: now, requests: [] });
    }

    const requestData = this.requestCounts.get(key);
    
    // Clean old requests (older than 1 minute)
    requestData.requests = requestData.requests.filter(time => now - time < 60000);
    
    // Check requests per minute
    if (requestData.requests.length >= this.config.maxRequestsPerMinute) {
      this.blockClient(clientId);
      return { allowed: false, reason: 'Rate limit exceeded', retryAfter: this.config.blockDuration };
    }

    // Check requests per second (last 1 second)
    const recentRequests = requestData.requests.filter(time => now - time < 1000);
    if (recentRequests.length >= this.config.maxRequestsPerSecond) {
      return { allowed: false, reason: 'Too many requests per second', retryAfter: 1000 };
    }

    // Check concurrent requests
    const concurrentKey = `${clientId}_concurrent`;
    const concurrent = this.requestQueue.get(concurrentKey) || 0;
    if (concurrent >= this.config.maxConcurrentRequests) {
      return { allowed: false, reason: 'Too many concurrent requests', retryAfter: 2000 };
    }

    // Allow request
    requestData.requests.push(now);
    requestData.count++;
    requestData.timestamp = now;
    
    return { allowed: true };
  }

  // Block client
  blockClient(clientId) {
    this.blockedIPs.add(clientId);
    this.requestCounts.set(`${clientId}_blocked`, { timestamp: Date.now() });
    console.warn(`Client ${clientId} has been temporarily blocked due to excessive requests`);
  }

  // Track concurrent request start
  startRequest(endpoint = 'default') {
    const clientId = this.getClientId();
    const key = `${clientId}_concurrent`;
    const current = this.requestQueue.get(key) || 0;
    this.requestQueue.set(key, current + 1);
  }

  // Track concurrent request end
  endRequest(endpoint = 'default') {
    const clientId = this.getClientId();
    const key = `${clientId}_concurrent`;
    const current = this.requestQueue.get(key) || 0;
    this.requestQueue.set(key, Math.max(0, current - 1));
  }

  // Cache management
  getCached(key) {
    const cached = this.cache.get(key);
    if (cached && Date.now() - cached.timestamp < this.config.cacheTimeout) {
      return cached.data;
    }
    this.cache.delete(key);
    return null;
  }

  setCached(key, data) {
    this.cache.set(key, {
      data,
      timestamp: Date.now()
    });
  }

  // Enhanced fetch with protection
  async protectedFetch(url, options = {}) {
    const endpoint = new URL(url, window.location.origin).pathname;
    
    // Check if request is allowed
    const permission = this.isRequestAllowed(endpoint);
    if (!permission.allowed) {
      throw new Error(`Request blocked: ${permission.reason}. Retry after ${permission.retryAfter}ms`);
    }

    // Check cache first
    const cacheKey = `${url}_${JSON.stringify(options)}`;
    const cached = this.getCached(cacheKey);
    if (cached && (!options.method || options.method === 'GET')) {
      return cached;
    }

    this.startRequest(endpoint);
    
    try {
      // Add timeout to request
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.config.requestTimeout);
      
      const response = await fetch(url, {
        ...options,
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.text(); // Returns string, not Response object
      
      // Cache successful GET requests
      if (!options.method || options.method === 'GET') {
        this.setCached(cacheKey, data);
      }
      
      return data; // Returns string
    } catch (error) {
      if (error.name === 'AbortError') {
        throw new Error('Request timeout');
      }
      throw error;
    } finally {
      this.endRequest(endpoint);
    }
  }

  // Retry mechanism
  async fetchWithRetry(url, options = {}) {
    let lastError;
    
    for (let attempt = 1; attempt <= this.config.retryAttempts; attempt++) {
      try {
        return await this.protectedFetch(url, options);
      } catch (error) {
        lastError = error;
        
        // Don't retry on rate limit or client errors
        if (error.message.includes('Request blocked') || 
            error.message.includes('HTTP 4')) {
          throw error;
        }
        
        if (attempt < this.config.retryAttempts) {
          const delay = this.config.retryDelay * Math.pow(2, attempt - 1); // Exponential backoff
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }
    }
    
    throw lastError;
  }

  // Cleanup old entries
  cleanup() {
    const now = Date.now();
    const oneHour = 60 * 60 * 1000;
    
    // Clean request counts
    for (const [key, data] of this.requestCounts.entries()) {
      if (now - data.timestamp > oneHour) {
        this.requestCounts.delete(key);
      }
    }
    
    // Clean cache
    for (const [key, data] of this.cache.entries()) {
      if (now - data.timestamp > this.config.cacheTimeout) {
        this.cache.delete(key);
      }
    }
    
    // Clean concurrent requests
    for (const [key, count] of this.requestQueue.entries()) {
      if (count === 0) {
        this.requestQueue.delete(key);
      }
    }
  }

  // Get statistics
  getStats() {
    const clientId = this.getClientId();
    return {
      clientId,
      isBlocked: this.blockedIPs.has(clientId),
      requestCounts: this.requestCounts.size,
      cacheSize: this.cache.size,
      concurrentRequests: this.requestQueue.get(`${clientId}_concurrent`) || 0
    };
  }
}

// Create singleton instance
const ddosProtection = new DDosProtection();
export default ddosProtection;